import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import { DEFAULT_ROW_HEIGHT } from "./adapter";

// Keep the skeleton dimensions in sync with the loaded grid.
const ROW_MARKER_WIDTH_PX = 40;
const MIN_COLUMN_WIDTH_PX = 104;
// Only the first few rows show placeholder bars; the rows below stay empty so
// the skeleton reads as a quiet sheet instead of generated noise.
const FILLED_ROW_COUNT = 5;
const CELL_BAR_CLASSNAME = "w-3/5";

const SKELETON_TAB_BAR_CLASSNAMES = ["w-16", "w-12", "w-14"];

const getGridTemplateColumns = (columnCount: number) =>
  `${ROW_MARKER_WIDTH_PX}px repeat(${columnCount}, minmax(0, 1fr))`;

interface SheetLoadingStateProps {
  fillHeight?: boolean;
}

export const SheetLoadingState = ({
  fillHeight = false,
}: SheetLoadingStateProps) => (
  <Stack
    fullWidth
    gap="none"
    className={cn(
      "min-h-0 min-w-0 overflow-hidden rounded-lg border border-stroke-tertiary bg-surface-primary",
      { "h-full": fillHeight },
    )}
    aria-busy="true"
  >
    <span className="sr-only">Loading spreadsheet</span>
    <FormulaBarSkeleton />
    <GridSkeleton fillHeight={fillHeight} />
    <SheetTabsSkeleton />
  </Stack>
);

const FormulaBarSkeleton = () => (
  <Stack
    direction="row"
    align="stretch"
    fullWidth
    gap="none"
    className="h-9 shrink-0 border-b border-stroke-tertiary bg-surface-primary"
  >
    <Stack
      justify="center"
      gap="none"
      className="w-24 shrink-0 border-r border-stroke-tertiary px-3"
    >
      <Skeleton className="h-3.5 w-8" />
    </Stack>
    <Stack
      align="center"
      justify="center"
      gap="none"
      className="w-9 shrink-0 border-r border-stroke-tertiary"
    >
      <Skeleton className="h-3.5 w-4" />
    </Stack>
    <Stack justify="center" gap="none" className="min-w-0 flex-1 px-3">
      <Skeleton className="h-3.5 w-3/5 max-w-[28rem]" />
    </Stack>
    <Stack
      align="center"
      justify="center"
      gap="none"
      className="w-10 shrink-0 border-l border-stroke-tertiary"
    >
      <Skeleton className="h-4 w-4" />
    </Stack>
  </Stack>
);

interface GridSkeletonProps {
  fillHeight: boolean;
}

const GridSkeleton = ({ fillHeight }: GridSkeletonProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(0);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;

    const measure = () => {
      setColumnCount(
        Math.max(
          1,
          Math.floor(
            (body.clientWidth - ROW_MARKER_WIDTH_PX) / MIN_COLUMN_WIDTH_PX,
          ),
        ),
      );
      setRowCount(
        Math.max(1, Math.floor(body.clientHeight / DEFAULT_ROW_HEIGHT)),
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const gridTemplateColumns = getGridTemplateColumns(columnCount);

  return (
    <Stack
      fullWidth
      gap="none"
      className={cn("min-w-0 bg-surface-primary", {
        "min-h-0 flex-1": fillHeight,
        "h-[600px]": !fillHeight,
      })}
    >
      <ColumnHeaderSkeleton
        columnCount={columnCount}
        gridTemplateColumns={gridTemplateColumns}
      />
      <Stack ref={bodyRef} fullWidth gap="none" className="min-h-0 flex-1">
        {Array.from({ length: rowCount }, (_, row) => (
          <SheetRowSkeleton
            key={row}
            isFilled={row < FILLED_ROW_COUNT}
            columnCount={columnCount}
            gridTemplateColumns={gridTemplateColumns}
          />
        ))}
      </Stack>
    </Stack>
  );
};

interface ColumnHeaderSkeletonProps {
  columnCount: number;
  gridTemplateColumns: string;
}

const ColumnHeaderSkeleton = ({
  columnCount,
  gridTemplateColumns,
}: ColumnHeaderSkeletonProps) => (
  <Stack
    align="stretch"
    gap="none"
    style={{ gridTemplateColumns }}
    className="grid h-9 w-full shrink-0 border-b border-stroke-tertiary bg-surface-secondary"
  >
    <Stack gap="none" className="border-r border-stroke-tertiary" />
    {Array.from({ length: columnCount }, (_, column) => (
      <Stack
        key={column}
        justify="center"
        gap="none"
        className="border-r border-stroke-tertiary px-3"
      >
        <Skeleton className="h-2.5 w-6" />
      </Stack>
    ))}
  </Stack>
);

interface SheetRowSkeletonProps {
  isFilled: boolean;
  columnCount: number;
  gridTemplateColumns: string;
}

const SheetRowSkeleton = ({
  isFilled,
  columnCount,
  gridTemplateColumns,
}: SheetRowSkeletonProps) => (
  <Stack
    align="stretch"
    gap="none"
    style={{ gridTemplateColumns, height: DEFAULT_ROW_HEIGHT }}
    className="grid w-full shrink-0 border-b border-stroke-tertiary"
  >
    <Stack
      justify="center"
      align="center"
      gap="none"
      className="border-r border-stroke-tertiary bg-surface-secondary"
    >
      <Skeleton className="h-2.5 w-3" />
    </Stack>
    {Array.from({ length: columnCount }, (_, column) => (
      <Stack
        key={column}
        justify="center"
        gap="none"
        className="border-r border-stroke-tertiary px-3"
      >
        {isFilled && <Skeleton className={cn("h-3", CELL_BAR_CLASSNAME)} />}
      </Stack>
    ))}
  </Stack>
);

const SheetTabsSkeleton = () => (
  <Stack
    direction="row"
    align="stretch"
    fullWidth
    gap="none"
    className="h-8 shrink-0 overflow-hidden border-t border-stroke-tertiary bg-surface-secondary"
  >
    <Stack
      align="center"
      justify="center"
      gap="none"
      className="w-10 shrink-0 border-r border-stroke-tertiary"
    >
      <Skeleton className="h-4 w-4" />
    </Stack>
    {SKELETON_TAB_BAR_CLASSNAMES.map((barClassName, tabIndex) => (
      <Stack
        key={tabIndex}
        justify="center"
        gap="none"
        className={cn("shrink-0 border-r border-stroke-tertiary px-4", {
          // The first tab reads as the active sheet, like the loaded tab list.
          "bg-surface-primary": tabIndex === 0,
        })}
      >
        <Skeleton className={cn("h-3", barClassName)} />
      </Stack>
    ))}
  </Stack>
);
